import * as THREE from 'three';
import type { SketchEllipseObject } from '../../../mesh/sketch/SketchEllipseObject';
import type { ProjectionResult } from '../model/ProjectionResult';
import type {
	ReferenceDrivenEllipseRelationRuntimeRecord,
	SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import { resolveSourceRefEntityId } from '../model/SketchEntityReferenceRecord';
import type { ExecutionDiagnostic } from '../model/SketchEntityReferenceDiagnostics';
import { BaseReferenceDrivenRelationService } from './BaseReferenceDrivenRelationService';
import { ReferenceDrivenEllipseCoincidentConstraint } from './constraints/ReferenceDrivenEllipseCoincidentConstraint';
import type {
	ReferenceDrivenEllipseRelationBinding,
	ReferenceDrivenEllipseRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenEllipseRelationService';

export type {
	ReferenceDrivenEllipseRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenEllipseRelationService';

/**
 * 参考驱动椭圆关系服务。
 */
export class ReferenceDrivenEllipseRelationService
	extends BaseReferenceDrivenRelationService<
		ReferenceDrivenEllipseRelationRuntimeRecord,
		ReferenceDrivenEllipseRelationBinding
	> {
	/**
	 * 挂载椭圆重合约束。
	 *
	 * @param input 挂载输入。
	 * @returns 挂载结果。
	 */
	public attach(input: {
		reference: SketchEntityReferenceRecord;
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'ellipse' }>;
		targetEllipse: SketchEllipseObject;
		runtimeSourceName?: string;
	}): ReferenceDrivenEllipseRelationAttachResult {
		this.detach({
			referenceId: input.reference.referenceId,
			targetEllipse: input.targetEllipse,
		});

		const record: ReferenceDrivenEllipseRelationRuntimeRecord = {
			referenceId: input.reference.referenceId,
			relationKind: 'coincident',
			driveMode: 'projection-drives-target',
			sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
			targetEntityId: input.targetEllipse.persistentId,
			constraintId: -1,
		};
		const constraint = new ReferenceDrivenEllipseCoincidentConstraint({
			referenceId: input.reference.referenceId,
			projectedCenterWorld: new THREE.Vector3(...input.projectionResult.geometryPayload.centerWorld),
			projectedRadiusX: input.projectionResult.geometryPayload.radiusX,
			projectedRadiusY: input.projectionResult.geometryPayload.radiusY,
			projectedRotation: input.projectionResult.geometryPayload.rotation,
			sourceDisplayLabel: input.reference.sourceDisplayLabel,
			runtimeSourceName: input.runtimeSourceName,
		});
		input.targetEllipse.constraints.addRelation(constraint);
		record.constraintId = constraint.id;
		this.setBinding(input.reference.referenceId, {
			constraint,
			record,
			targetEllipse: input.targetEllipse,
		});

		/** 椭圆定义由批次统一参数求解写回，挂载阶段只建立公式关系。 */
		return {
			record,
			diagnostics: [],
		};
	}

	/**
	 * 拆除椭圆关系。
	 *
	 * @param input 拆除输入。
	 * @returns 诊断信息。
	 */
	public detach(input: {
		referenceId: string;
		targetEllipse?: SketchEllipseObject;
	}): ExecutionDiagnostic[] {
		const binding = this.getBinding(input.referenceId);
		if (binding) {
			binding.targetEllipse.constraints.removeRelation(binding.record.constraintId);
			this.deleteBinding(input.referenceId);
			return [];
		}

		if (input.targetEllipse) {
			const constraint = input.targetEllipse.constraints.getRelations().find((candidate) => (
				candidate instanceof ReferenceDrivenEllipseCoincidentConstraint
				&& candidate.referenceId === input.referenceId
			));
			if (constraint) {
				input.targetEllipse.constraints.removeRelation(constraint.id);
			}
		}
		return [];
	}
}
